import React, { useEffect, useState } from "react";
import { ShieldCheck, UserX, UserCheck, KeyRound, LogOut, RefreshCw, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { PageHeader, Loading, DataTable, StatusBadge, Card } from "../components/common";
import { toast } from "sonner";

export default function PortalUserManager() {
  const { t } = useI18n();
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [audit, setAudit] = useState([]);
  const [err, setErr] = useState("");

  const load = () => {
    api.get("/pum/users").then((r) => setUsers(r.data)).catch((e) => setErr(e.response?.data?.detail || "Portal session required — sign in via Secure Access"));
    api.get("/pum/sessions").then((r) => setSessions(r.data)).catch(() => {});
    api.get("/security-audit").then((r) => setAudit(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const act = async (uid, action, extra = {}) => {
    try { await api.patch(`/pum/users/${uid}`, { action, ...extra }); toast.success(`${action.replace("_", " ")} done`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };
  const revoke = async (sid) => { try { await api.post(`/pum/sessions/${sid}/revoke`); toast.success("Session revoked"); load(); } catch (e) { toast.error("Failed"); } };

  if (err) return (
    <div>
      <PageHeader title="Portal User Manager" subtitle="Two-layer secure administration" />
      <Card><div className="grid place-items-center py-16 text-center text-slate-500"><ShieldCheck className="mb-3 h-10 w-10 text-[var(--qatar)]" /><p className="text-sm">{err}</p><a href="/gate" className="qatar-gradient mt-4 rounded-xl px-5 py-2.5 text-sm font-semibold text-white">Go to Secure Two-Layer Access</a></div></Card>
    </div>
  );
  if (!users) return <Loading />;

  const userCols = [
    { key: "name", label: "Name", render: (r) => <span className="font-medium">{r.name}</span> },
    { key: "email", label: "Email" },
    { key: "role", label: "Role", render: (r) => <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">{r.portal_role || r.role}</span> },
    { key: "disabled", label: "Status", render: (r) => <StatusBadge status={r.disabled ? "Blocked" : "Active"} /> },
    { key: "active_sessions", label: "Sessions" },
    { key: "last_login", label: "Last Login", render: (r) => <span className="text-xs">{r.last_login}</span> },
    { key: "actions", label: "Actions", render: (r) => (
      <div className="flex gap-1">
        {r.disabled
          ? <button data-testid={`enable-${r.id}`} onClick={() => act(r.id, "enable")} title="Enable" className="rounded p-1.5 text-emerald-600 hover:bg-emerald-50"><UserCheck className="h-4 w-4" /></button>
          : <button data-testid={`disable-${r.id}`} onClick={() => act(r.id, "disable")} title="Disable" className="rounded p-1.5 text-red-600 hover:bg-red-50"><UserX className="h-4 w-4" /></button>}
        <button data-testid={`resetmfa-${r.id}`} onClick={() => act(r.id, "reset_mfa")} title="Reset MFA" className="rounded p-1.5 text-amber-600 hover:bg-amber-50"><KeyRound className="h-4 w-4" /></button>
        <button data-testid={`revoke-user-${r.id}`} onClick={() => act(r.id, "revoke_sessions")} title="Revoke sessions" className="rounded p-1.5 text-slate-600 hover:bg-slate-100"><LogOut className="h-4 w-4 flip-x" /></button>
      </div>
    ) },
  ];

  const sessCols = [
    { key: "user_email", label: "User" },
    { key: "role", label: "Role" },
    { key: "auth_level", label: "Auth Level", render: (r) => <StatusBadge status={r.auth_level === "PORTAL_ACCESS_GRANTED" ? "Approved" : r.auth_level} /> },
    { key: "revoked", label: "Status", render: (r) => <StatusBadge status={r.revoked ? "Blocked" : "Active"} /> },
    { key: "created_at", label: "Created", render: (r) => <span className="text-xs">{new Date(r.created_at).toLocaleString()}</span> },
    { key: "actions", label: "", render: (r) => !r.revoked && <button data-testid={`revoke-sess-${r.id}`} onClick={() => revoke(r.id)} className="rounded-lg border border-slate-200 px-2 py-1 text-xs hover:bg-slate-50">Revoke</button> },
  ];

  const auditCols = [
    { key: "timestamp", label: "Time", render: (r) => <span className="font-mono text-xs">{new Date(r.timestamp).toLocaleString()}</span> },
    { key: "event", label: "Event", render: (r) => <span className="font-medium">{r.event}</span> },
    { key: "role", label: "Role" },
    { key: "result", label: "Result", render: (r) => <StatusBadge status={r.result === "SUCCESS" ? "Passed" : "Failed"} /> },
    { key: "ip", label: "IP", render: (r) => <span className="font-mono text-xs">{r.ip}</span> },
    { key: "correlation_id", label: "Correlation", render: (r) => <span className="font-mono text-[10px] text-slate-400">{(r.correlation_id || "").slice(0, 8)}</span> },
  ];

  return (
    <div>
      <PageHeader title="Portal User Manager" subtitle="Manage portal users, sessions and security audit (server-side RBAC)" summary="Portal user manager. Create, enable, disable users, reset MFA, revoke sessions and review the security audit log." />
      <div className="mb-4 flex gap-1 border-b border-slate-200">
        {[["users", "Users"], ["sessions", "Sessions"], ["audit", "Security Audit"]].map(([k, l]) => (
          <button key={k} data-testid={`pum-tab-${k}`} onClick={() => setTab(k)} className={`border-b-2 px-4 py-2.5 text-sm font-medium ${tab === k ? "border-[var(--qatar)] text-[var(--qatar)]" : "border-transparent text-slate-500"}`}>{l}</button>
        ))}
      </div>
      {tab === "users" && <DataTable testid="pum-users-table" columns={userCols} rows={users} pageSize={15} />}
      {tab === "sessions" && <DataTable testid="pum-sessions-table" columns={sessCols} rows={sessions} pageSize={15} searchable={false} />}
      {tab === "audit" && <DataTable testid="pum-audit-table" columns={auditCols} rows={audit} pageSize={20} />}
    </div>
  );
}
